import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar.jsx';
import Footer from './components/Footer.jsx';
import Home from './pages/Home.jsx';
import Services from './pages/Services.jsx';
import Blog from './pages/Blog.jsx';
import CaseStudies from './pages/CaseStudies.jsx';
import Contact from './pages/Contact.jsx';

// Individual blog posts
import SuiteCRMAIAge from './pages/blogs/SuiteCRMAIAge.jsx';
import CRMProcessNotSoftware from './pages/blogs/CRMProcessNotSoftware.jsx';
import DonorRetentionSuiteCRM from './pages/blogs/DonorRetentionSuiteCRM.jsx';
import ModernizingBusinessOperations from './pages/blogs/ModernizingBusinessOperations.jsx';
import OpenClawSuiteCRMIntegration from './pages/blogs/OpenClawSuiteCRMIntegration.jsx';

// Individual case studies
import HealthcareScheduling from './pages/case-studies/HealthcareScheduling.jsx';
import EntertainmentCRM from './pages/case-studies/EntertainmentCRM.jsx';
import LinkedInAutomation from './pages/case-studies/LinkedInAutomation.jsx';
import FinanceAutomation from './pages/case-studies/FinanceAutomation.jsx';

/**
 * The root component orchestrates the assembly of all sections of the site. It
 * manages global behaviours such as scroll listeners that add a 'scrolled' class
 * to the navbar once the page moves past a threshold, and reveal animations when
 * sections enter the viewport via an IntersectionObserver.
 */
const App = () => {

  // Add or remove scrolled class from navbar
  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById('navbar');
      if (window.scrollY > 50) {
        navbar?.classList.add('scrolled');
      } else {
        navbar?.classList.remove('scrolled');
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Apply a simple parallax effect to elements with the data-parallax attribute
  useEffect(() => {
    const parallaxEls = document.querySelectorAll('[data-parallax]');
    const onScroll = () => {
      const scrollY = window.scrollY;
      parallaxEls.forEach((el) => {
        const speed = parseFloat(el.getAttribute('data-parallax')) || 0.1;
        el.style.transform = `translateY(${scrollY * speed}px)`;
      });
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Natural page opening from top whenever the route changes
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location]);

  // IntersectionObserver to reveal elements on initial load and whenever the route changes.
  useEffect(() => {
    const revealElements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [location]);

  const handleLinkClick = () => {
    // Optional callback for handling link clicks globally
  };

  return (
    <>
      <Navbar onLinkClick={handleLinkClick} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/contact" element={<Contact />} />
        {/* Individual blog post routes */}
        <Route path="/blog/suitecrm-ai-age" element={<SuiteCRMAIAge />} />
        <Route path="/blog/crm-process-not-software" element={<CRMProcessNotSoftware />} />
        <Route path="/blog/donor-retention-suitecrm" element={<DonorRetentionSuiteCRM />} />
        <Route path="/blog/modernizing-business-operations" element={<ModernizingBusinessOperations />} />
        <Route path="/blog/openclaw-suitecrm-integration" element={<OpenClawSuiteCRMIntegration />} />
        {/* Individual case study routes */}
        <Route path="/case-studies/healthcare-scheduling" element={<HealthcareScheduling />} />
        <Route path="/case-studies/entertainment-crm" element={<EntertainmentCRM />} />
        <Route path="/case-studies/linkedin-automation" element={<LinkedInAutomation />} />
        <Route path="/case-studies/finance-automation" element={<FinanceAutomation />} />
      </Routes>
      <Footer />
    </>
  );
};

export default App;
